/**
 * Subscription presentation layer (ADR-003) — the CLIENT half of the
 * token→subscription cutover. Mirrors the server capability engine
 * (server/_core/lib/entitlements.ts) so the UI can say "included in your plan"
 * / "upgrade to unlock" instead of "N tokens".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️  SINGLE FLAG: SUBSCRIPTION_MODE
 *     false (TODAY) → every surface renders the existing TOKEN language, byte
 *       for byte. Importing/using these helpers changes NOTHING while off.
 *     true (after the supervised cutover) → the same surfaces render SUBSCRIPTION
 *       language. Flip this together with the server flag ENTITLEMENTS_ENFORCED,
 *       with Ali watching a live payment test — never one without the other, or
 *       the UI would claim "included" while the engine still charges (or vice
 *       versa). See docs/constitution/tokens-to-subscription-execution.md.
 *
 * This module is PURE and framework-free (no React, no i18next) so it can be
 * imported anywhere and unit-reasoned about. Copy is passed `isAr` and returned
 * as plain strings; western digits only (project rule #6).
 */

// ── Capability matrix — MUST stay in lockstep with the server ────────────────
// (server/_core/lib/entitlements.ts · PLAN_CAPABILITIES). If you change one,
// change both; the server is the real gate, this is only presentation.

export type Capability =
  | 'radar'
  | 'resume'
  | 'cover_letter'
  | 'linkedin'
  | 'posts'
  | 'carousel'
  | 'campaign'
  | 'career_plan';

export type PlanId = 'free' | 'starter' | 'growth' | 'enterprise';

export const PLAN_CAPABILITIES: Record<PlanId, ReadonlySet<Capability>> = {
  free: new Set<Capability>(['radar']),
  starter: new Set<Capability>(['radar', 'resume', 'cover_letter', 'linkedin']),
  growth: new Set<Capability>([
    'radar', 'resume', 'cover_letter', 'linkedin', 'posts', 'carousel', 'career_plan',
  ]),
  enterprise: new Set<Capability>([
    'radar', 'resume', 'cover_letter', 'linkedin', 'posts', 'carousel', 'campaign', 'career_plan',
  ]),
};

/**
 * Plan display metadata (DRAFT — the capability grid + prices Ali approved
 * 2026-07-22; confirm names/prices against the live `plans` table at the
 * supervised cutover before this text ships visible). Prices are monthly SAR.
 */
export interface PlanMeta {
  id: PlanId;
  name_ar: string;
  name_en: string;
  price_sar: number | null; // null = free tier
}

export const PLAN_META: Record<PlanId, PlanMeta> = {
  free: { id: 'free', name_ar: 'استكشف', name_en: 'Explore', price_sar: null },
  starter: { id: 'starter', name_ar: 'الانطلاق', name_en: 'Starter', price_sar: 149 },
  growth: { id: 'growth', name_ar: 'النمو', name_en: 'Growth', price_sar: 299 },
  enterprise: { id: 'enterprise', name_ar: 'المؤسسات', name_en: 'Enterprise', price_sar: null },
};

const KNOWN_PLANS = new Set<string>(['free', 'starter', 'growth', 'enterprise']);

/**
 * Legacy plan ids (AuthContext still types `plan` as 'free'|'starter'|'pro'|
 * 'elite'). They MUST alias to the nearest current tier — coercing to 'free'
 * would show a paying legacy user as locked out. Mirrors the sidebar labels:
 * pro→starter, elite→growth. Keep in lockstep with the server entitlements.
 */
const PLAN_ALIASES: Record<string, PlanId> = { pro: 'starter', elite: 'growth' };

/** Coerce any stored plan value (plan_id / current_plan / plan / null) to a
 *  PlanId; legacy ids alias to the nearest tier; unknown → 'free'. */
export function normalizePlan(plan: string | null | undefined): PlanId {
  if (!plan) return 'free';
  if (KNOWN_PLANS.has(plan)) return plan as PlanId;
  return PLAN_ALIASES[plan] ?? 'free';
}

/** Does this plan include this capability? Pure mirror of the server `can`. */
export function can(plan: string | null | undefined, capability: Capability): boolean {
  return PLAN_CAPABILITIES[normalizePlan(plan)].has(capability);
}

/** Capabilities included in a plan, in a stable display order. */
export function capabilitiesFor(plan: string | null | undefined): Capability[] {
  const set = PLAN_CAPABILITIES[normalizePlan(plan)];
  return CAPABILITY_ORDER.filter((c) => set.has(c));
}

/** The lowest plan that includes a capability (for "upgrade to X" copy). */
export function lowestPlanFor(capability: Capability): PlanId {
  for (const p of ['free', 'starter', 'growth', 'enterprise'] as PlanId[]) {
    if (PLAN_CAPABILITIES[p].has(capability)) return p;
  }
  return 'enterprise';
}

// ── Copy ─────────────────────────────────────────────────────────────────────

const CAPABILITY_ORDER: Capability[] = [
  'radar', 'resume', 'cover_letter', 'linkedin', 'career_plan', 'posts', 'carousel', 'campaign',
];

/** User-facing capability names (checklist of "what your plan includes"). */
export const CAPABILITY_LABELS: Record<Capability, { ar: string; en: string }> = {
  radar: { ar: 'تشخيص ملفك المهني', en: 'Career profile diagnostic' },
  resume: { ar: 'بناء وتخصيص السيرة', en: 'Resume builder & tailoring' },
  cover_letter: { ar: 'خطاب التقديم', en: 'Cover letters' },
  linkedin: { ar: 'تحسين لينكدإن', en: 'LinkedIn optimization' },
  posts: { ar: 'منشورات لينكدإن', en: 'LinkedIn posts' },
  carousel: { ar: 'الكاروسيل', en: 'Carousels' },
  campaign: { ar: 'حملات التواصل', en: 'Outreach campaigns' },
  career_plan: { ar: 'خطتي للوصول إلى هدفي', en: 'My career plan' },
};

export function capabilityLabel(capability: Capability, isAr: boolean): string {
  const l = CAPABILITY_LABELS[capability];
  return isAr ? l.ar : l.en;
}

export function planName(plan: string | null | undefined, isAr: boolean): string {
  const m = PLAN_META[normalizePlan(plan)];
  return isAr ? m.name_ar : m.name_en;
}

/**
 * The single helper every "cost chip" calls. Returns what the chip should say
 * for a given capability, given the viewer's plan.
 *
 *   SUBSCRIPTION_MODE === false → { kind: 'token' }  → caller shows its existing
 *     "N نقطة" chip exactly as today (this function does not touch that path).
 *   SUBSCRIPTION_MODE === true  → 'included' when the plan has the capability,
 *     else 'locked' with an "upgrade to <plan>" label.
 */
export type AccessChip =
  | { kind: 'token' }
  | { kind: 'included'; label: string }
  | { kind: 'locked'; label: string; upgradeTo: PlanId };

export function describeAccess(
  plan: string | null | undefined,
  capability: Capability,
  isAr: boolean,
): AccessChip {
  if (!SUBSCRIPTION_MODE) return { kind: 'token' };
  if (can(plan, capability)) {
    return { kind: 'included', label: isAr ? 'مشمول في باقتك' : 'Included in your plan' };
  }
  const upgradeTo = lowestPlanFor(capability);
  const name = planName(upgradeTo, isAr);
  return {
    kind: 'locked',
    label: isAr ? `متاح في باقة ${name}` : `Available on ${name}`,
    upgradeTo,
  };
}

// ── The flag ─────────────────────────────────────────────────────────────────
// Keep LAST so the helpers above read top-to-bottom before the reader hits the
// switch. Flip with the server's ENTITLEMENTS_ENFORCED, supervised, at cutover.
export const SUBSCRIPTION_MODE = true;
